import React, { useEffect } from "react";
import "./App.less";

import { Layout, Menu, Button, Spin, Drawer } from "antd";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Link,
  withRouter,
  useHistory,
  useLocation,
} from "react-router-dom";
import { About } from "./pages/About";
import { OnlineConvertor } from "./pages/OnlineConvertor";
import { LandingPage } from "./pages/LandingPage";
import { TermsPage } from "./pages/TermsPage";
import AuthCallback from "./components/AuthCallback";
import { TermsLink } from "./components/TermsLink";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { isElectron } from "./isElectron";
import { UserOutlined, LogoutOutlined, MenuOutlined } from "@ant-design/icons";
import { masavFileApiService } from "./functions/apiService";

const RootRedirect = () => {
  const { isAuthenticated, loading } = useAuth();
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    // Don't redirect while still loading
    if (loading) {
      return;
    }

    // Check if there are authentication parameters in the URL
    const urlParams = new URLSearchParams(location.search);
    const authCode = urlParams.get('code');
    const authError = urlParams.get('error');

    if (authCode || authError) {
      // If there are auth parameters, redirect to the auth callback
      history.replace('/auth/callback');
    } else if (isAuthenticated) {
      // If authenticated, redirect to the main page
      history.replace('/online-builder');
    } else {
      // If not authenticated and no auth params, redirect to landing page
      history.replace('/landing');
    }
  }, [isAuthenticated, loading, location.search, history]);

  // Show loading while determining redirect
  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100%',
      minHeight: 0,
      flex: 1,
      background: '#f0f2f5'
    }}>
      <Spin size="large" />
    </div>
  );
};

const Login = () => {
  const { isAuthenticated, signIn, loading, awsConfigured } = useAuth();
  const history = useHistory();

  useEffect(() => {
    // If user is already authenticated, redirect to the online converter
    if (isAuthenticated) {
      history.replace('/online-builder');
      return;
    }

    // Check for auth parameters in URL
    const urlParams = new URLSearchParams(window.location.search);
    const authCode = urlParams.get('code');
    const authError = urlParams.get('error');
    
    // If there are auth parameters, don't auto-sign in - let the redirect handle it
    if (authCode || authError) {
      return;
    }

    // Only attempt sign in if AWS is configured, not loading, not already authenticated, and no auth code in URL
    if (!loading && !isAuthenticated && awsConfigured) {
      const autoSignIn = async () => {
        try {
          await signIn();
        } catch (error) {
          console.error('Auto sign in error:', error);
        }
      };
      autoSignIn();
    }
  }, [isAuthenticated, loading, awsConfigured, history, signIn]);

  // Show minimal loading state while auto-redirecting
  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100%',
      minHeight: 0,
      flex: 1,
      background: '#f0f2f5'
    }}>
      <Spin size="large" />
    </div>
  );
};

const MenuComponent = withRouter(({ history }) => {
  const { t } = useTranslation();
  const { user, signOut, isAuthenticated } = useAuth();
  const [drawerVisible, setDrawerVisible] = React.useState(false);
  const [usageInfo, setUsageInfo] = React.useState(null);
  const isMountedRef = React.useRef(false);

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const fetchUsageInfo = React.useCallback(async () => {
    if (!isAuthenticated) {
      if (isMountedRef.current) {
        setUsageInfo(null);
      }
      return null;
    }

    try {
      console.log("🔵 App.js - Fetching usage info...");
      const info = await masavFileApiService.getUsageInfo();
      console.log("✅ App.js - Received usage info:", info);
      if (isMountedRef.current) {
        setUsageInfo(info);
      }
      return info;
    } catch (error) {
      console.error("❌ App.js - Failed to fetch MASAV usage info:", error);
      console.error("❌ App.js - Error details:", {
        message: error.message,
        statusCode: error.statusCode,
        responseData: error.responseData,
        responseText: error.responseText,
      });
      // Don't set usageInfo to null - let it show default or previous value
      return null;
    }
  }, [isAuthenticated]);

  React.useEffect(() => {
    fetchUsageInfo();
  }, [fetchUsageInfo]);

  React.useEffect(() => {
    window.refreshMasavUsage = fetchUsageInfo;
    return () => {
      if (window.refreshMasavUsage === fetchUsageInfo) {
        delete window.refreshMasavUsage;
      }
    };
  }, [fetchUsageInfo]);

  const renderUsageInfo = () => {
    if (!usageInfo) {
      return null;
    }

    const accountType = usageInfo.accountType || "free";
    const isFree = accountType === "free";
    const accountTypeLabel = isFree
      ? t("account-type-free")
      : t("account-type-premium");
    let remainingLabel = null;

    if (isFree) {
      // If no record exists yet (null or undefined), show 10 downloads remaining (default)
      // After first download, the record will be created and we'll have actual numbers
      const remaining =
        typeof usageInfo.remainingDownloads === "number"
          ? usageInfo.remainingDownloads
          : usageInfo.remainingDownloads === null && usageInfo.downloadCount === null
          ? 10 // No record created yet - default to 10
          : usageInfo.monthlyLimit || 10; // Fallback to monthlyLimit or 10
      remainingLabel = t("remaining-downloads", { count: remaining });
    } else {
      remainingLabel = t("remaining-downloads-unlimited");
    }

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          fontSize: "12px",
          lineHeight: 1.2,
          color: "#fff",
        }}
      >
        <span>{accountTypeLabel}</span>
        <span>{remainingLabel}</span>
      </div>
    );
  };


  const handleSignOut = async () => {
    try {
      await signOut();
      // AuthContext will handle the redirect with timeout
    } catch (error) {
      console.error('Sign out error:', error);
      // If there's an error, redirect to root as fallback
      window.location.href = '/';
    }
  };


  const navigationItems = [
    {
      key: "/online-builder",
      icon: <img src={`${process.env.PUBLIC_URL || ''}/icon clear-01.png`} alt="logo" style={{ height: "20px", width: "20px" }} />,
      label: (
        <Link to="/online-builder" onClick={() => setDrawerVisible(false)}>
          {t("online-editor")}
        </Link>
      ),
    },
    {
      key: "/about",
      label: (
        <Link to="/about" onClick={() => setDrawerVisible(false)}>
          {t("about")}
        </Link>
      ),
    },
    {
      key: "/lang",
      label: isElectron ? (
        <Button
          style={{ background: "none", border: "none", color: "#000", padding: 0 }}
          onClick={() => {
            localStorage.setItem("@language", t("other-lang-code"));
            window.location.reload();
          }}
        >
          {t("other-lang")}
        </Button>
      ) : (
        <a
          href={`${window.location.pathname}?lang=${t("other-lang-code")}`}
          onClick={() => setDrawerVisible(false)}
        >
          {t("other-lang")}
        </a>
      ),
    },
    // Sign out at the bottom
    ...(isAuthenticated ? [{
      type: 'divider',
    }, {
      key: 'signout',
      icon: <LogoutOutlined />,
      label: 'Sign Out',
      onClick: () => {
        setDrawerVisible(false);
        handleSignOut();
      },
      danger: true
    }] : []),
  ];

  return (
    <>
      <div style={{ 
        background: '#001529', 
        padding: '0 16px', 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '64px',
        direction: 'ltr' // Force LTR for hamburger menu to ensure it stays on the left in RTL mode
      }}>
        {/* Hamburger menu - hidden for now, Drawer and pages remain available */}
        <Button
          type="text"
          icon={<MenuOutlined />}
          onClick={() => setDrawerVisible(true)}
          style={{ color: '#fff', fontSize: '18px', display: 'none' }}
        />
        {isAuthenticated && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              color: "#fff",
            }}
          >
            {renderUsageInfo()}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: 500,
              }}
            >
              <UserOutlined />
              <span>
                {user?.attributes?.email ||
                  user?.email ||
                  user?.username ||
                  "User"}
              </span>
            </div>
            <Button
              type="text"
              className="header-sign-out-btn"
              icon={<LogoutOutlined />}
              onClick={handleSignOut}
              size="small"
            >
              Sign Out
            </Button>
          </div>
        )}
      </div>

      <Drawer
        title={t("menu")}
        placement="left"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        bodyStyle={{ padding: 0 }}
      >
        <Menu
          mode="vertical"
          selectedKeys={[history?.location?.pathname]}
          items={navigationItems}
          style={{ border: 'none' }}
        />
      </Drawer>
    </>
  );
});

const AppContent = () => {
  const { t } = useTranslation();

  useEffect(() => {
    document.getElementById("seo-title").innerHTML = t("seo-title");
    document.getElementById("seo-description").content = t("seo-description");
  }, [t]);
  
  return (
    <div
      className="app-root-fill"
      style={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        maxWidth: "100%",
      }}
    >
      <Router>
        <Layout
          style={{
            flex: 1,
            minHeight: 0,
            height: "100%",
            maxHeight: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            maxWidth: "100%",
          }}
        >
          <Switch>
            <Route path="/landing" component={LandingPage} />
            <Route path="/terms" component={TermsPage} />
            <Route path="/login" component={Login} />
            <Route path="/auth/callback" component={AuthCallback} />
            <Route path="*">
              <div
                className="app-main-shell"
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                <MenuComponent />
                <div
                  className="main-app-content"
                  style={{
                    padding: "0 50px",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    maxWidth: "100%",
                    overflow: "hidden",
                    boxSizing: "border-box",
                  }}
                >
                  <Switch>
                    <ProtectedRoute path="/online-builder" component={OnlineConvertor} />
                    <ProtectedRoute path="/about" component={About} />
                    <Route path="/" component={RootRedirect} />
                  </Switch>
                </div>
                <footer
                  style={{
                    textAlign: "center",
                    padding: "4px 8px",
                    flexShrink: 0,
                    lineHeight: 1.2,
                  }}
                >
                  <TermsLink style={{ fontSize: "12px" }} />
                </footer>
              </div>
            </Route>
          </Switch>
        </Layout>
      </Router>
    </div>
  );
};

const App = () => {
  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </div>
  );
};

export default App;
